import { z } from 'zod';
import { dateStringSchema } from './dateString.js';
import { QBO_PNL_GROUPS } from './pnlSection.js';
import { reportPeriodSchema } from './reportPeriod.js';
const taxonomyTypeLikeSchema = z.enum(['income', 'expense']);
export const financialReportRowSchema = z.object({
    label: z.string(),
    values: z.array(z.number()),
    indent: z.number(),
    isSummary: z.boolean(),
    isSectionHeader: z.boolean().optional(),
});
export const financialReportSectionSchema = z.object({
    title: z.string(),
    rows: z.array(financialReportRowSchema),
    summary: financialReportRowSchema.nullable(),
    group: z.enum(QBO_PNL_GROUPS).optional(),
});
export const financialReportResponseSchema = z.object({
    reportName: z.string(),
    reportDate: dateStringSchema,
    fetchedAt: z.date(),
    columns: z.array(z.string()),
    sections: z.array(financialReportSectionSchema),
    source: z.enum(['direct', 'fallback']).optional(),
});
export const budgetNotConfiguredSchema = z.object({
    type: z.literal('BudgetNotConfigured'),
});
export const financialReportUnavailableSchema = z.object({
    type: z.literal('SnapshotUnavailable'),
    message: z.string(),
});
const agingAmountsSchema = z.object({
    current: z.number(),
    days1to30: z.number(),
    days31to60: z.number(),
    days61to90: z.number(),
    days91AndOver: z.number(),
    total: z.number(),
});
export const arAgingRowSchema = agingAmountsSchema.extend({
    externalCustomerId: z.string(),
    customerName: z.string(),
});
export const arAgingReportSchema = z.object({
    reportDate: dateStringSchema,
    fetchedAt: z.date(),
    rows: z.array(arAgingRowSchema),
    grandTotal: agingAmountsSchema,
});
export const apAgingRowSchema = agingAmountsSchema.extend({
    externalVendorId: z.string(),
    vendorName: z.string(),
});
export const apAgingReportSchema = z.object({
    reportDate: dateStringSchema,
    fetchedAt: z.date(),
    rows: z.array(apAgingRowSchema),
    grandTotal: agingAmountsSchema,
});
export const taxonomyPnlMonthlyRowSchema = z.object({
    type: taxonomyTypeLikeSchema,
    category: z.string(),
    subcategory: z.string(),
    actualAmount: z.number(),
    budgetAmount: z.number(),
});
export const taxonomyPnlMonthSchema = z.object({
    label: z.string(),
    month: z.number().int().min(1).max(12),
    year: z.number().int(),
    rows: z.array(taxonomyPnlMonthlyRowSchema),
});
export const taxonomyPnlMonthlyOutputSchema = z.object({
    budgetDisplayId: z.string().nullable(),
    budgetName: z.string().nullable(),
    budgetFiscalYear: z.number().nullable(),
    fetchedAt: z.date(),
    months: z.array(taxonomyPnlMonthSchema),
    totalRows: z.array(taxonomyPnlMonthlyRowSchema),
});
export const financialReportSnapshotTypeSchema = z.enum([
    'budget-vs-actual',
    'profit-and-loss',
    'balance-sheet',
    'ar-aging',
    'ap-aging',
    'general-ledger',
    'bank-reconciliation',
]);
export const financialReportSnapshotParametersSchema = z.discriminatedUnion('reportType', [
    z.object({
        reportType: z.literal('budget-vs-actual'),
        source: z.enum(['teo', 'qbo']),
        period: reportPeriodSchema.nullable(),
        budgetDisplayId: z.string().nullable(),
    }),
    z.object({
        reportType: z.literal('profit-and-loss'),
        period: reportPeriodSchema,
    }),
    z.object({
        reportType: z.literal('balance-sheet'),
        asOfDate: dateStringSchema,
    }),
    z.object({
        reportType: z.literal('ar-aging'),
        reportDate: dateStringSchema,
    }),
    z.object({
        reportType: z.literal('ap-aging'),
        reportDate: dateStringSchema,
    }),
    z.object({
        reportType: z.literal('general-ledger'),
        startDate: dateStringSchema,
        endDate: dateStringSchema,
    }),
    z.object({
        reportType: z.literal('bank-reconciliation'),
    }),
]);
export const financialReportSnapshotPayloadSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('financial-report'),
        data: financialReportResponseSchema,
    }),
    z.object({
        kind: z.literal('budget-not-configured'),
        data: budgetNotConfiguredSchema,
    }),
    z.object({
        kind: z.literal('ar-aging'),
        data: arAgingReportSchema,
    }),
    z.object({
        kind: z.literal('ap-aging'),
        data: apAgingReportSchema,
    }),
    z.object({
        kind: z.literal('taxonomy-pnl-monthly'),
        data: taxonomyPnlMonthlyOutputSchema,
    }),
]);
//# sourceMappingURL=financialReports.js.map